import { ButtonProps, Card, ContainerProps } from '@chakra-ui/react';
import React from 'react';
import { FieldValues, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { ActionButtons, Form, Locale, RadioButtonField } from '../../shared';
import KeyFeatures from './KeyFeatures';
import PriceHeader from './PriceHeader';
import SmsCounter from './SmsCounter';
import translation from './lib/translations';

interface ITariffPlan {
  id: string;
  name: string;
  isActive: boolean;
  price: number;
  activeUsers: number;
  sms: number;
  type: string;
}

interface IPricingWidgetFormProps extends ContainerProps {
  subscriptionPlans: [ITariffPlan];
  smsPlan: ITariffPlan;
  onSubmit?: (data: {}) => void;
  isLoading?: boolean;
  subscribeButtonProps?: ButtonProps;
  locale: Locale | string;
}

const PricingWidgetForm: React.FC<IPricingWidgetFormProps> = ({
  subscriptionPlans,
  smsPlan,
  onSubmit,
  isLoading,
  subscribeButtonProps = {},
  locale,
  ...props
}) => {
  const t = translation(locale);
  const defaultValues: FieldValues = {
    subscriptionId: subscriptionPlans[0].id,
    smsPackages: [],
  };

  const methods = useForm({ defaultValues });
  const selectedSubscriptionId = useWatch({ name: 'subscriptionId', control: methods.control });
  const selectedSubscription = subscriptionPlans.find(({ id }) => id === selectedSubscriptionId);

  const { fields, append, remove } = useFieldArray({
    name: 'smsPackages',
    control: methods.control,
  });

  const summaryPrice = selectedSubscription!.price + fields.length * smsPlan?.price;
  const summarySms = selectedSubscription!.sms + fields.length * smsPlan?.sms;

  return (
    <Card
      gap='xl'
      p={{ base: 'lg', md: '2xl' }}
      variant='solid'
      w={{ base: 'full', md: '30rem' }}
      {...props}
    >
      <PriceHeader
        summaryPrice={summaryPrice}
        month={t.month}
      />
      <Form
        childrenContainerProps={{
          gap: 'lg',
        }}
        formId='payment'
        methods={methods}
        onSubmit={(data) => onSubmit?.(data)}
      >
        <ActionButtons
          mb='xs'
          mt='0'
          positiveButtonProps={{
            isLoading,
            type: 'submit',
            width: 'full',
            variant: 'primary',
            ...subscribeButtonProps,
          }}
          positiveButtonText={t.subscribeButtonText}
        />
        <RadioButtonField
          label={t.monthlyActiveUsersLabel}
          name='subscriptionId'
          options={subscriptionPlans.map(({ activeUsers, id }) => ({
            label: activeUsers,
            value: id,
          }))}
          onClick={() => remove()}
        />
        <SmsCounter
          addPackage={() => append({ price: smsPlan.price })}
          isDisableMinus={!fields.length}
          minusPackage={() => remove(0)}
          summarySms={summarySms}
          locale={locale}
        />
        <KeyFeatures locale={locale} />
      </Form>
    </Card>
  );
};

export default PricingWidgetForm;
